import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from "@heroicons/react/16/solid";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from "@moe/oss/ui/context-menu";
import { useKeyHeld } from "@moe/priv/hooks/use-key-held";
import { Character } from "@moe/priv/model/character";
import { cn } from "@moe/priv/utils";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { AuthModal } from "@web/components/AuthModal";
import { CharacterCard } from "@web/components/CharacterCard";
import { ReportModal } from "@web/components/ReportModal";
import { VerticalCardView } from "@web/components/VerticalCardView";
import { useCreateChat } from "@web/hooks/use-create-chat";
import { useLikeChar } from "@web/hooks/use-like-char";
import { useTagNavigation } from "@web/route-services/index/use-tag-navigation";
import { useAppContext } from "@web/route-services/root/AppContext";
import { EyeIcon, LinkIcon } from "lucide-react";
import { motion } from "motion/react";
import { toast } from "sonner";

interface Props {
  char?: Character;
  isPending: boolean;
  className?: string;
  [key: string]: any;
}

export function VerticalCard({ char, className, isPending, ...rest }: Props) {
  if (isPending) return <Loading className={className} />;
  if (char) return <Loaded char={char} className={className} {...rest} />;
}

function Loading({ className }: { className?: string }) {
  return (
    <motion.div
      className={cn(
        "rounded-4xl bg-moe-card border-line flex  w-full cursor-pointer flex-col items-center overflow-hidden border shadow-sm md:w-[232px]",
        className
      )}
      initial={{ opacity: 0.7 }}
      animate={{ opacity: [0.7, 1, 0.7] }}
      transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
    >
      <div className="bg-muted h-[205px] w-full rounded md:w-[232px] " />
      <div className="flex h-[145px] w-full grow flex-col px-4 pb-3 pt-3">
        <div className="bg-muted mb-2 h-[20px] w-full rounded" />
        <div className="bg-muted mb-2 h-[18px] w-1/2 rounded" />
        <div className="bg-muted mb-1 h-[16px] w-full rounded" />
        <div className="bg-muted h-[16px] w-4/5 rounded" />
        <div className="mt-auto flex flex-row justify-end space-x-3">
          <div className="bg-muted h-4 w-8 rounded" />
          <div className="bg-muted h-4 w-8 rounded" />
        </div>
      </div>
    </motion.div>
  );
}

function Loaded({ char, className, ...rest }: Omit<Props, "char"> & { char: Character }) {
  const { createModal, doFocusExploreRef, session } = useAppContext();
  const cardClickHandler = () => {
    createModal({
      content: <CharacterCard char={char} />
    });
  };
  const { add, only } = useTagNavigation();
  const isCtrlHeld = useKeyHeld("Control");

  const { likeCount, isLiked, like: handleLike } = useLikeChar({ char: char });
  const handleChat = useCreateChat();

  const handleTagSelect = (tag: string) => {
    doFocusExploreRef.current = true;
    if (isCtrlHeld) {
      add(tag);
      return;
    }
    only(tag);
  };

  const [_, copyToClipboard] = useCopyToClipboard();

  const handleShare = async () => {
    await copyToClipboard(`${window.location.origin}/character/${char.id}`);
    toast("Share link copied!");
  };

  const handleOpenInNewTab = () => {
    window.open(`/character/${char.id}`, "_blank", "noopener");
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    // Middle click (button 1)
    if (e.button === 1) {
      e.preventDefault();
      handleOpenInNewTab();
    }
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger className={cn("flex w-full focus:outline-none md:w-[232px]", className)}>
        <VerticalCardView
          title={char.title}
          tags={char.tags}
          tagline={char.tagline}
          avatar={char.avatar}
          createdBy={char.createdBy}
          externalCreatedBy={char.externalCreatedBy}
          likeCount={likeCount}
          isLiked={isLiked}
          messageCount={char.messageCount}
          onShare={handleShare}
          className={className}
          onLike={handleLike}
          onChat={() => handleChat(char)}
          onTagSelect={handleTagSelect}
          onClick={cardClickHandler}
          onMouseDown={handleMouseDown}
          {...rest}
        />
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={handleOpenInNewTab}>
          <ArrowTopRightOnSquareIcon className="mr-2 h-4 w-4" />
          Open in new tab
        </ContextMenuItem>
        <ContextMenuItem onClick={cardClickHandler}>
          <EyeIcon className="mr-2 h-4 w-4" />
          View details
        </ContextMenuItem>
        <ContextMenuItem onClick={() => (window.location.href = `/character/${char.id}`)}>
          <LinkIcon className="mr-2 h-4 w-4" />
          Go to page
        </ContextMenuItem>
        <ContextMenuItem
          onClick={() => {
            if (!session) {
              createModal({ content: <AuthModal formKind="signup" /> });
              return;
            }
            createModal({ content: <ReportModal characterID={char.id} /> });
          }}
        >
          <ExclamationTriangleIcon className="mr-2 h-4 w-4" />
          Report
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
